import { useEffect, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import Bugsnag from '@bugsnag/js'

import { ForwardToInbox, InsertDriveFile } from '@mui/icons-material'
import { Box, Icon, Typography, useMediaQuery, useTheme } from '@mui/material'

import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import DashboardContainer from 'components/smart/dashboard-container/DashboardContainer'
import ReportEmployeeModal from '../components/ReportEmployeeModal'
import AdxEmptyStateV2 from 'components/reusable/emptyState/AdxEmptyStateV2'

import {
  downloadAnnualReport,
  downloadEDataPraisi,
  dropdownYearlyReport,
  emailAnnualReport,
} from 'services/api/modules/payroll/payroll-yearly-report'
import AdxButton from 'components/custom/AdxButton'
import { downloadResponseFile } from 'services/helper/fileDownload'

const YearlyReport = () => {
  const [isModalOpen, setModalOpen] = useState(false)
  const [loadingButton, setLoadingButton] = useState(false)
  const [loadingEdataPraisiButton, setLoadingEdataPraisiButton] = useState(false)

  const [modalProps, setModalProps] = useState({
    titleHeader: '',
    descriptionHeader: '',
    icon: null,
    actionDescription: '',
    actionButton: '',
  })

  const handleCloseModal = () => {
    setModalOpen(false)
  }

  const theme = useTheme()
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'))
  const isMedium = useMediaQuery(theme.breakpoints.down('lg'))

  const [selectYear, setSelectYear] = useState('')
  const handleSetSelectValue = (e) => {
    setSelectYear(e.target.value)
  }

  const { data: yearDropdown } = useQuery(
    ['getYearDropdown'],
    async () => {
      const { data } = await dropdownYearlyReport()
      return data
    },
    {
      onError: (err) => Bugsnag.notify(err),
    }
  )

  useEffect(() => {
    if (yearDropdown) {
      if (yearDropdown?.length !== 0) {
        setSelectYear(yearDropdown[0])
      } else {
        setSelectYear('')
      }
    } else {
      setSelectYear('')
    }
  }, [yearDropdown])

  const { mutate: mutateDownloadAnnualReport } = useMutation(downloadAnnualReport, {
    onSuccess: (res) => {
      downloadResponseFile(res, {
        fileName: 'annual_report.zip',
        successMessage: 'Annual report downloaded.',
      })
      setLoadingButton(false)
      setModalOpen(false)
    },
    onError: (error) => {
      setLoadingButton(false)
      Bugsnag.notify(error)
    },
  })

  const handleDownloadAnnualReport = (values) => {
    setLoadingButton(true)
    const { staff_ids, ...rest } = values
    if (staff_ids?.length !== 0) {
      mutateDownloadAnnualReport(values)
    } else {
      mutateDownloadAnnualReport(rest)
    }
  }

  const { mutate: mutateEmailAnnualReport } = useMutation((val) => emailAnnualReport(val), {
    onSuccess: () => {
      setLoadingButton(false)
      setModalOpen(false)
    },
    onError: (error) => {
      setLoadingButton(false)
      Bugsnag.notify(error)
    },
  })

  const handleEmail = (values) => {
    setLoadingButton(true)
    const { staff_ids, ...rest } = values
    if (staff_ids?.length !== 0) {
      mutateEmailAnnualReport(values)
    } else {
      mutateEmailAnnualReport(rest)
    }
  }

  const { refetch } = useQuery(
    ['downloadEDataPraisi'],
    () => downloadEDataPraisi(parseInt(selectYear)),
    {
      enabled: false,
      onSuccess: (res) => {
        downloadResponseFile(res, {
          fileName: 'edata_praisi',
          contentType: res.headers['content-type'],
          successMessage: 'E-Data Praisi downloaded.',
        })
        setLoadingEdataPraisiButton(false)
      },
      onError: (error) => {
        setLoadingEdataPraisiButton(false)
        Bugsnag.notify(error)
      },
    }
  )

  const handleDownloadEDataPraisi = () => {
    setLoadingEdataPraisiButton(true)
    refetch()
  }

  const FormComponent = ({ title, description, children, sx }) => {
    return (
      <Box
        display="flex"
        flexDirection={isLarge ? 'row' : isMedium && 'column'}
        rowGap={1}
        alignItems={isLarge ? 'center' : isMedium && 'flex-start'}
        justifyContent={isLarge ? 'space-between' : isMedium && 'flex-start'}
        padding={2}
        sx={{
          width: 1,
          borderBottom: 1,
          borderColor: 'gray-line.main',
          ...sx,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: isLarge ? 3 / 5 : 1,
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              marginRight: 2,
              minWidth: 60,
              height: 60,
              borderRadius: 50,
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: 'alternate.light',
              color: 'alternate.main',
            }}
          >
            <Icon
              sx={{
                fontSize: 35,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <InsertDriveFile />
            </Icon>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography noWrap variant="h4" fontWeight="bold">
              {title}
            </Typography>
            <Typography variant="body1" color="light-gray-text.main">
              {description}
            </Typography>
          </Box>
        </Box>
        <Box
          justifyContent={'flex-end'}
          marginLeft={isLarge && 2}
          sx={{
            width: isLarge ? 2 / 5 : 1,
            display: 'flex',
          }}
        >
          {children}
        </Box>
      </Box>
    )
  }
  return (
    <DashboardContainer headerTitle="Yearly Report">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box
          display={{ xs: 'flex', lg: 'block' }}
          sx={{
            justifyContent: { xs: 'space-between', lg: 'none' },
            alignItems: { xs: 'center' },
          }}
        >
          {yearDropdown?.length !== 0 && (
            <Box display="flex" alignItems="center" sx={{ gap: 2 }}>
              <Typography>{isMedium ? 'Year: ' : 'Statement year: '}</Typography>
              <AdxDropdown
                sx={{
                  width: '150px',
                  '& .MuiOutlinedInput-root': {
                    maxHeight: '45px',
                    outline: 'none',
                    backgroundColor: 'white.main',
                  },
                  '& .MuiSelect-select': {
                    padding: '0px 14px',
                  },
                }}
                value={selectYear}
                onChange={handleSetSelectValue}
                disabled={yearDropdown?.length === 0}
              >
                {yearDropdown?.map((year, index) => (
                  <AdxMenuItem key={index} value={year} selectedvalue={selectYear}>
                    {year}
                  </AdxMenuItem>
                ))}
              </AdxDropdown>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            bgcolor: 'white.main',
            border: 1,
            borderColor: 'gray-line.main',
            borderRadius: '4.5px',
          }}
          paddingY={isMedium && 2}
          maxHeight={'calc(100vh - 197px)'}
          overflow={'auto'}
          className="scrollbar"
        >
          {yearDropdown?.length !== 0 ? (
            <>
              <FormComponent
                title="EA Form"
                description="Statement of remuneration from employment - Private Sector"
              >
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  sx={{ width: 1, gap: 1, mt: isMedium && 1 }}
                >
                  <AdxButton
                    variant={'outlined'}
                    onClick={() => {
                      setModalProps({
                        titleHeader: `${selectYear} EA Form`,
                        descriptionHeader: 'Download EA Form as pdf',
                        icon: <ForwardToInbox />,
                        actionDescription: 'Download document for:',
                        actionButton: 'Download',
                        form: 'ea_form',
                        year: selectYear,
                        function: handleDownloadAnnualReport,
                      })
                      setModalOpen(true)
                    }}
                    size={isLarge ? 'standard' : 'small'}
                    sx={{ width: isLarge ? '160px' : 1 }}
                  >
                    Download
                  </AdxButton>
                  <AdxButton
                    variant={'contained'}
                    onClick={() => {
                      setModalProps({
                        titleHeader: `${selectYear} EA Form`,
                        descriptionHeader: 'Send EA Form to employee via email',
                        icon: <ForwardToInbox />,
                        actionDescription: 'Send EA Form to:',
                        actionButton: 'Send',
                        form: 'ea_form',
                        year: selectYear,
                        function: handleEmail,
                      })
                      setModalOpen(true)
                    }}
                    size={isLarge ? 'standard' : 'small'}
                    sx={{ width: isLarge ? '160px' : 1 }}
                  >
                    Send via Email
                  </AdxButton>
                </Box>
              </FormComponent>
              <FormComponent
                title="PCB 2(II)"
                description="Statement of payment by the employer for the employee(s) during the duration of assessment"
              >
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  sx={{ width: 1, gap: 1, mt: isMedium && 1 }}
                >
                  <AdxButton
                    variant={'outlined'}
                    onClick={() => {
                      setModalProps({
                        titleHeader: `${selectYear} PCB 2(II)`,
                        descriptionHeader: 'Download PCB 2(II) as pdf',
                        icon: <ForwardToInbox />,
                        actionDescription: 'Download document for:',
                        actionButton: 'Download',
                        form: 'pcb2',
                        year: selectYear,
                        function: handleDownloadAnnualReport,
                      })
                      setModalOpen(true)
                    }}
                    size={isLarge ? 'standard' : 'small'}
                    sx={{ width: isLarge ? '160px' : 1 }}
                  >
                    Download
                  </AdxButton>
                  <AdxButton
                    variant={'contained'}
                    onClick={() => {
                      setModalProps({
                        titleHeader: `${selectYear} PCB 2(II)`,
                        descriptionHeader: 'Send PCB 2(II) to employee via email',
                        icon: <ForwardToInbox />,
                        actionDescription: 'Send PCB 2(II) to:',
                        actionButton: 'Send',
                        form: 'pcb2',
                        year: selectYear,
                        function: handleEmail,
                      })
                      setModalOpen(true)
                    }}
                    size={isLarge ? 'standard' : 'small'}
                    sx={{ width: isLarge ? '160px' : 1 }}
                  >
                    Send via Email
                  </AdxButton>
                </Box>
              </FormComponent>
              <FormComponent
                title="E-Data Praisi"
                description="Statement of payment by the employer for the employee(s) during the duration of assessment"
                sx={{ borderBottom: 'none' }}
              >
                <AdxButton
                  variant={'contained'}
                  size={isLarge ? 'standard' : 'small'}
                  onClick={handleDownloadEDataPraisi}
                  isLoading={loadingEdataPraisiButton}
                  disabled={loadingEdataPraisiButton}
                  sx={{ mt: isMedium && 1, width: isLarge ? '160px' : '100%' }}
                >
                  Download
                </AdxButton>
              </FormComponent>
            </>
          ) : (
            <AdxEmptyStateV2
              icon={<Icon>account_balance</Icon>}
              title="No Yearly Report Found"
              subtitle="There is no yearly report to be generated at this moment"
              height={400}
            />
          )}
          <ReportEmployeeModal
            isModalOpen={isModalOpen}
            handleClose={handleCloseModal}
            modalProps={modalProps}
            handleSubmission={modalProps?.function}
            isSubmitRequest={loadingButton}
          />
        </Box>
      </Box>
    </DashboardContainer>
  )
}

export default YearlyReport
